/**
 * Windows policies as this app calls the API: a customer's templates, one PC's assignment and
 * compliance, and its escrowed BitLocker recovery keys. The on-demand check (which also makes the agent
 * apply the policy) answers with a result whatever happens, so its button never leaves the status line
 * on "Checking…".
 */
import type {
  AssignWindowsPolicyRequest,
  BitLockerKeyDto,
  BitLockerKeyRevealDto,
  RevealRecoveryKeyRequest,
  DeviceWindowsPolicyDto,
  SaveWindowsPolicyRequest,
  WindowsPolicyCheckResult,
  WindowsPolicyDto,
  WindowsPolicySaveResult
} from '../contracts.gen.js';
import { ApiError } from './auth.js';
import { request } from './http.js';

export function getWindowsPolicies(customerId: number, signal?: AbortSignal): Promise<WindowsPolicyDto[]> {
  return request<WindowsPolicyDto[]>(`/api/customers/${customerId}/windows-policies`, { signal });
}

export function createWindowsPolicy(customerId: number, req: SaveWindowsPolicyRequest): Promise<WindowsPolicySaveResult> {
  return request<WindowsPolicySaveResult>(`/api/customers/${customerId}/windows-policies`, { method: 'POST', body: req });
}

export function updateWindowsPolicy(policyId: number, req: SaveWindowsPolicyRequest): Promise<WindowsPolicySaveResult> {
  return request<WindowsPolicySaveResult>(`/api/windows-policies/${policyId}`, { method: 'PUT', body: req });
}

export function deleteWindowsPolicy(policyId: number): Promise<void> {
  return request<void>(`/api/windows-policies/${policyId}`, { method: 'DELETE' });
}

export function assignUnassignedPcs(policyId: number): Promise<{ assigned: number; policy: WindowsPolicyDto }> {
  return request(`/api/windows-policies/${policyId}/assign-unassigned`, { method: 'POST' });
}

export function getDeviceWindowsPolicy(deviceId: number, signal?: AbortSignal): Promise<DeviceWindowsPolicyDto> {
  return request<DeviceWindowsPolicyDto>(`/api/devices/${deviceId}/windows-policy`, { signal });
}

export function assignWindowsPolicy(deviceId: number, policyId: number | null): Promise<DeviceWindowsPolicyDto> {
  const body: AssignWindowsPolicyRequest = { policyId };
  return request<DeviceWindowsPolicyDto>(`/api/devices/${deviceId}/windows-policy/assign`, { method: 'POST', body });
}

export function getRecoveryKeys(deviceId: number, signal?: AbortSignal): Promise<BitLockerKeyDto[]> {
  return request<BitLockerKeyDto[]>(`/api/devices/${deviceId}/windows-policy/recovery-keys`, { signal });
}

/** Admin only, and recorded in the audit log. */
export function revealRecoveryKey(deviceId: number, protectorId: string): Promise<BitLockerKeyRevealDto> {
  const body: RevealRecoveryKeyRequest = { protectorId };
  return request<BitLockerKeyRevealDto>(`/api/devices/${deviceId}/windows-policy/recovery-keys/reveal`, { method: 'POST', body });
}

export async function checkWindowsPolicy(deviceId: number): Promise<WindowsPolicyCheckResult> {
  try {
    return await request<WindowsPolicyCheckResult>(`/api/devices/${deviceId}/windows-policy/check`, { method: 'POST' });
  } catch (error) {
    const message = error instanceof ApiError
      ? error.message || 'The check could not be run.'
      : `The CRM could not be reached. (${error instanceof Error ? error.message : String(error)})`;
    return { success: false, error: message, compliance: null };
  }
}
